/**
 * React-Query hooks for `/api/market/*` (PULSE-211).
 *
 * Coinglass-style aggregate market data: candles + cross-exchange
 * aggregate sub-series (OI / Net Delta / L-S / liquidations / funding)
 * on one shared time grid. All admin-gated.
 */
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'

export interface MarketSymbol {
  symbol: string
  turnover_24h: number
}

export interface Candle {
  t: number; o: number; h: number; l: number; c: number; v: number; turn: number
}
export interface OiPoint { t: number; v: number; n_ex: number; o?: number; h?: number; l?: number; c?: number }
/** PULSE-381: cvd = Net Delta (CoinGlass, монеты), nd = её изменение за бар, td = дельта тейкеров $ */
export interface CvdPoint { t: number; nd: number; cvd: number; td?: number; o?: number; h?: number; l?: number; c?: number }
export interface LsrPoint { t: number; long_pct: number; ratio: number | null }
export interface LiqPoint { t: number; long: number; short: number }
export interface FundingPoint { t: number; v: number }
export interface TakerPoint { t: number; buy: number; sell: number }

export interface ActiveStatus {
  is_active: boolean | null
  last_seen: number | null   // unix seconds
}

export interface MarketSeries {
  symbol: string
  tf: string
  /** PULSE-381: биржа свечей и Net Delta (binance, fallback bybit) */
  venue?: string
  candles: Candle[]
  oi: OiPoint[]
  cvd: CvdPoint[]
  lsr: LsrPoint[]
  liq: LiqPoint[]
  funding: FundingPoint[]
  taker?: TakerPoint[]
  /** PULSE-384: спотовые тейкеры Binance (USD), только tf ≥ 1m. */
  spot_taker?: TakerPoint[]
  active?: ActiveStatus
}

export const TIMEFRAMES = ['1m', '5m', '15m', '1h', '4h'] as const
export type Timeframe = (typeof TIMEFRAMES)[number]

export function useMarketSymbols() {
  return useQuery({
    queryKey: ['market', 'symbols'],
    queryFn: async () =>
      (await api.get<{ symbols: MarketSymbol[] }>('/market/symbols')).data.symbols,
    staleTime: 60_000,
  })
}

export function useMarketSeries(symbol: string, tf: Timeframe, limit = 300, anchor?: number | null) {
  return useQuery({
    queryKey: ['market', 'series', symbol, tf, limit, anchor ?? null],
    queryFn: async () =>
      (await api.get<MarketSeries>(`/market/${symbol}/series`, {
        params: anchor ? { tf, limit, anchor } : { tf, limit },
      })).data,
    enabled: !!symbol,
    // исторические окна (anchor) не меняются — не перезапрашиваем
    refetchInterval: anchor ? false : 30_000,
    staleTime: anchor ? 10 * 60_000 : 15_000,
  })
}

/** PULSE-404: сделки Ильи (friend_trips). fills — исполнения трипа: open = вход/добор, close = выход/частичное. */
export interface FriendFill { t: number; px: number; kind: 'open' | 'close'; q: number }
export interface FriendTrade {
  id: number; symbol: string; side: 'short' | 'long'
  entry_t: number; exit_t: number
  first_px: number; entry_avg: number; exit_avg: number
  n_open: number; n_close: number
  notional_usd: number; pnl_usd: number; ret_pct: number
  fills: FriendFill[]
}
export function useFriendTrades(limit = 30) {
  return useQuery({
    queryKey: ['market', 'friend-trades', limit],
    queryFn: async () =>
      (await api.get<{ built_at: string | null; trades: FriendTrade[] }>('/market/friend-trades', { params: { limit } })).data,
    staleTime: 5 * 60_000,
  })
}

export interface MarketHeatmap {
  symbol: string
  empty: boolean
  times: number[]          // unix seconds, one per column (old → new)
  bin_prices: number[]     // price per row (top → bottom = high → low)
  grid: number[][]         // grid[row][col], intensity 0..levels
  levels: number
  priceline: number[]      // price per column
  cur: number | null
  pmin: number | null
  pmax: number | null
  n_ex?: number
}

export function useMarketHeatmap(symbol: string, tf: Timeframe, limit = 300) {
  return useQuery({
    queryKey: ['market', 'heatmap', symbol, tf, limit],
    queryFn: async () =>
      (await api.get<MarketHeatmap>(`/market/${symbol}/heatmap`, {
        params: { tf, limit },
      })).data,
    enabled: !!symbol,
    // Heatmap is heavier; server caches 180s, so don't poll harder.
    refetchInterval: 180_000,
    staleTime: 120_000,
  })
}
